import { useFiles as useFinderFiles } from 'hooks/queries/finder/useFiles'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { getTotalCountFromInfiniteQuery } from 'utils/query'
import { SectionHeader } from '../SectionHeader/SectionHeader'
import FilesTree from '../FilesTree/FilesTree'
import { FilesTreeProvider } from '../FilesTree/stores/FilesTreeStore'
import Styles from './AllFiles.module.scss'
import { Button } from 'blaze-ui/Button'
import { useLeftSidebarChannel } from 'hooks/useLeftSidebarChannel'
import { Loading } from '../FilesTree/Loading/Loading'
import { CanView } from 'components/Can'
import { RootState, UserRole } from 'types'
import { NavMenuAllFilesItem } from 'blaze-components/WorkspaceDashboardSidebar/components/NavMenu/Buttons/NavMenuAllFilesItem'
import { WorkspaceService } from 'api/services/WorkspaceService'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useSelector } from 'react-redux'
import WorkspaceDashboardSidebarContentStyles from '../../WorkspaceDashboardSidebarContent.module.scss'
import {
  NewProjectButton,
  NewProjectIconButton,
} from 'blaze-components/WorkspaceDashboardSidebar/components/AllFiles/NewProjectButton'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { NewProjectDrawer } from 'components/Wizards/WizardsDrawer/NewProjectDrawer'
import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { NavMenuUploadsItem } from 'blaze-components/WorkspaceDashboardSidebar/components/NavMenu/Buttons/NavMenuUploadsItem'
import { NavMenuArchiveItem } from 'blaze-components/WorkspaceDashboardSidebar/components/NavMenu/Buttons/NavMenuArchiveItem'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import cx from 'classnames'
import { BlazeNewButton } from '../NavMenu/Buttons/BlazeNewButton'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { NavMenuSearchItem } from '../NavMenu/Buttons/NavMenuSearchItem'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useFiles as useWorkspaceFiles } from 'hooks/queries/files/useFiles'
import { VM_ROOT_FOLDERS_QUERY } from '../FilesTree/vmFiles'

const AllFiles = observer(() => {
  const { drawersStore } = useRootStore()
  const location = 'root_folders'
  const [isOpen, setIsOpen] = useState(true)
  const workspaceId = useCurrentWorkspaceId()
  const workspace = useCurrentWorkspace()
  const { isMobile } = useResponsiveDesign()
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const isVirtualMarketerWorkspace = workspace?.is_virtual_marketer === true
  const isNonVirtualMarketerWorkspace = workspace?.is_virtual_marketer === false
  const canSeeArchive = useFlipperFlag('archive_page')

  const toggleSection = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen)
  }

  useLeftSidebarChannel(location)
  const queryOptions = { items: 20 }

  const finderAllFilesQuery = useFinderFiles(
    {
      location,
      workspaceId,
      query: queryOptions,
      disableSchema: true,
    },
    { enabled: isNonVirtualMarketerWorkspace },
  )
  const virtualMarketerAllFilesQuery = useWorkspaceFiles(
    {
      items: queryOptions.items,
      q: VM_ROOT_FOLDERS_QUERY,
    },
    { enabled: isVirtualMarketerWorkspace },
  )
  const allFilesQuery = isVirtualMarketerWorkspace ? virtualMarketerAllFilesQuery : finderAllFilesQuery
  const { fetchNextPage, hasNextPage, isFetching, isFetched, isFetchingNextPage } = allFilesQuery
  const allFilesCount = isVirtualMarketerWorkspace
    ? getTotalCountFromInfiniteQuery(virtualMarketerAllFilesQuery)
    : getTotalCountFromInfiniteQuery(finderAllFilesQuery)
  const { sidebarLocked, floatingSidebar } = useSelector(
    ({ sidebar: { locked: sidebarLocked }, floatingSidebar }: RootState) => ({
      floatingSidebar,
      sidebarLocked,
    }),
  )
  const closeMobileSidebar = () => {
    if (isMobile && (sidebarLocked || floatingSidebar)) WorkspaceService.hideSidebar()
  }

  if (isFetched && allFilesCount < 1) {
    return null
  }

  return (
    <div className={cx(WorkspaceDashboardSidebarContentStyles.section, 'home-tour-3')}>
      <SectionHeader
        label="Files & Projects"
        onPress={toggleSection}
        isExpanded={isOpen}
        action={
          <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspaceId}>
            <ResponsiveDesign.Mobile>
              {workspace?.is_virtual_marketer ? null : (
                <NewProjectIconButton iconSize={20} onPress={() => drawersStore.openDrawer(NewProjectDrawer)} />
              )}
            </ResponsiveDesign.Mobile>
            <ResponsiveDesign.Desktop>
              <NewProjectButton />
            </ResponsiveDesign.Desktop>
          </CanView>
        }
      />
      <ul className={Styles.topContainer}>
        {isSystemRedesignEnabled && (
          <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspaceId}>
            <li>
              <BlazeNewButton onPress={closeMobileSidebar} isNavMenuItem />
            </li>
          </CanView>
        )}
        {isSystemRedesignEnabled && (
          <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspaceId}>
            <li>
              <NavMenuSearchItem />
            </li>
          </CanView>
        )}
        <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspaceId}>
          <li>
            <NavMenuAllFilesItem workspaceId={workspaceId} onPress={closeMobileSidebar} />
          </li>
        </CanView>
        {workspace?.is_virtual_marketer && isMobile ? null : (
          <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspaceId}>
            <li>
              <NavMenuUploadsItem workspaceId={workspaceId} onPress={closeMobileSidebar} />
            </li>
          </CanView>
        )}
        {canSeeArchive && (
          <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspaceId}>
            <li>
              <NavMenuArchiveItem workspaceId={workspaceId} onPress={closeMobileSidebar} />
            </li>
          </CanView>
        )}
      </ul>
      <FilesTreeProvider>
        {isOpen && (
          <FilesTree
            workspaceId={workspaceId}
            location={location}
            queryOptions={queryOptions}
            isVirtualMarketer={isVirtualMarketerWorkspace}
            useFinderQuery={isNonVirtualMarketerWorkspace}
          />
        )}
        {!!hasNextPage && (
          <>
            {!isFetchingNextPage && !isFetching && (
              <div className={Styles.loadMore}>
                <Button variant="tertiary" size="sm" onPress={() => fetchNextPage()}>
                  More files
                </Button>
              </div>
            )}
            <div className={Styles.loading}>{isFetchingNextPage && <Loading rows={2} />}</div>
          </>
        )}
      </FilesTreeProvider>
    </div>
  )
})

export { AllFiles }
